import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../../styles/LabTestRequestForm.css';
import { useAuth } from '../../hooks/useAuth';
import PatientHistorySummary from '../Patients/PatientHistorySummary';
import { LAB_TEST_SIDES } from '../../utils/labTestDisplayName';

const projectionNeedsSide = (attribute, sideRequired) =>
  sideRequired && (attribute === 'AP' || attribute === 'LAT');

function SideSelector({ value, onChange }) {
  return (
    <div className="side-selector">
      <span className="side-selector-label">Side:</span>
      <div className="side-selector-options" role="group" aria-label="Side">
        {LAB_TEST_SIDES.map((side) => (
          <button
            key={side}
            type="button"
            className={`side-option${value === side ? ' selected' : ''}`}
            aria-pressed={value === side}
            onClick={() => onChange(side)}
          >
            {side}
          </button>
        ))}
      </div>
    </div>
  );
}

const LabTestRequestForm = ({ patient: patientProp, admissionId: admissionIdProp, onClose }) => {
  const { user, token, hasPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Support both prop-based (modal) and route-based (page) usage
  const patientFromState = patientProp || location.state?.patient || null;
  const admissionIdFromState = admissionIdProp || location.state?.admissionId || null;
  const handleClose = onClose || (() => navigate(-1));
  
  // Local state
  const [patient, setPatient] = useState(patientFromState || null);
  const [labTests, setLabTests] = useState([]);
  const [selectedTests, setSelectedTests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [branch, setBranch] = useState('');
  const [testHistory, setTestHistory] = useState({});
  const [showHistory, setShowHistory] = useState(false);
  
  // New state: active tab (Laboratory or Radiology)
  const [activeTab, setActiveTab] = useState("Laboratory");

  // Helper to check range flag (from LabTestReport)
  const checkRangeFlag = (valueStr, rangeStr) => {
    if (!rangeStr || !valueStr) return '';
    const match = rangeStr.match(/([\d.]+)\s*-\s*([\d.]+)/);
    if (!match) return '';
    const minVal = parseFloat(match[1]);
    const maxVal = parseFloat(match[2]);
    const val = parseFloat(valueStr);
    if (isNaN(minVal) || isNaN(maxVal) || isNaN(val)) return '';
    if (val < minVal) return 'L';
    if (val > maxVal) return 'H';
    return '';
  };

  // Calculate days ago from a date
  const daysAgo = (dateStr) => {
    const testDate = new Date(dateStr);
    const now = new Date();
    const diffTime = Math.abs(now - testDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Process CBC results (JSON format) to extract out-of-range values
  const processCBCResults = (resultStr) => {
    try {
      const results = JSON.parse(resultStr);
      const outOfRange = [];
      
      results.forEach(item => {
        if (item.testValue && item.range) {
          const flag = checkRangeFlag(item.testValue, item.range);
          if (flag) {
            outOfRange.push({
              name: item.testName,
              value: item.testValue,
              flag: flag
            });
          }
        }
      });
      
      return outOfRange;
    } catch (e) {
      // If parsing fails, return empty array
      return [];
    }
  };

  // Format badge text for CBC test
  const formatCBCBadge = (history) => {
    const outOfRange = processCBCResults(history.last_result);
    const days = daysAgo(history.last_date);
    
    if (outOfRange.length === 0) {
      return `Completed (${days}d)`;
    }
    
    // Show up to 3 out-of-range values
    const displayItems = outOfRange.slice(0, 3);
    const badges = displayItems.map(item => 
      `${item.name}: ${item.value}${item.flag}`
    ).join(', ');
    
    const moreCount = outOfRange.length - displayItems.length;
    const moreText = moreCount > 0 ? ` +${moreCount} more` : '';
    return `${badges}${moreText} (${days}d)`;
  };

  // Get the CSS class for CBC badge based on out-of-range results
  const getCBCBadgeClass = (history) => {
    const outOfRange = processCBCResults(history.last_result);
    if (outOfRange.length === 0) return '';
    
    // If any are high, use 'h', otherwise use 'l'
    const hasHigh = outOfRange.some(item => item.flag === 'H');
    return hasHigh ? 'h' : 'l';
  };

  // Fetch lab tests
  const fetchLabTests = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/lab/labTests`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLabTests(response.data);
    } catch (error) {
      console.error('Error fetching lab tests:', error);
      toast.error('Error fetching lab tests.');
    }
  };

  // Fetch historical test results for patient
  const fetchTestHistory = async (patientId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/lab/patient/${patientId}/test-history`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Convert array to object keyed by test_id for easy lookup
      const historyMap = {};
      response.data.forEach(item => {
        historyMap[item.test_id] = item;
      });
      setTestHistory(historyMap);
    } catch (error) {
      console.error('Error fetching test history:', error);
      // Don't show error toast, just fail silently as this is optional data
    }
  };

  useEffect(() => {
    if (patientFromState) {
      Promise.all([
        fetchLabTests(),
        fetchTestHistory(patientFromState.patient_id)
      ])
        .catch((err) => console.error(err))
        .finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, [token, patientFromState]);

  // Handle checkbox changes
  const handleCheckboxChange = (testId, testName, checked, sideRequired = false, siblingIds = []) => {
    if (checked) {
      setSelectedTests((prev) => {
        if (prev.some((test) => test.Lab_Test === testId)) return prev;
        const siblingSide = prev.find((test) => siblingIds.includes(test.Lab_Test) && test.side)?.side || null;
        return [...prev, { Lab_Test: testId, Test_Description: '', side: siblingSide, sideRequired }];
      });
    } else {
      setSelectedTests((prev) => prev.filter((test) => test.Lab_Test !== testId));
    }
  };

  const handleSideChange = (testIds, side) => {
    const ids = new Set(testIds);
    setSelectedTests((prev) => prev.map((test) => (
      ids.has(test.Lab_Test) ? { ...test, side } : test
    )));
  };

  // Submit selected tests and branch
  const submitRequest = async () => {
    if (!branch) {
      toast.error('Please select a Branch before submitting.');
      return;
    }
    if (selectedTests.length === 0) {
      toast.warn('Please select at least one test before submitting.');
      return;
    }
    if (selectedTests.some((test) => test.sideRequired && !test.side)) {
      toast.error('Select a side (L, R, or Both) for each projection that requires one.');
      return;
    }

    setIsSubmitting(true);
    const requestData = {
      patient_id: patient ? patient.patient_id : null,
      tests: selectedTests.map(({ Lab_Test, Test_Description, side }) => ({
        Lab_Test,
        Test_Description,
        side: side || null,
      })),
      updatedBy: user.portal_id,
      requested_by: user.name,
      requested_branch: branch,
      request_type: activeTab,
      admission_id: admissionIdFromState
    };

    try {
      await axios.post(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/lab/lab-requests`,
        requestData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Request submitted successfully.');
      setTimeout(() => handleClose(), 1500);
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error('Error submitting request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Cancel the request with a confirmation if there are unsaved changes.
  const cancelRequest = () => {
    if (selectedTests.length > 0) {
      const confirmCancel = window.confirm(
        'You have unsaved changes. Are you sure you want to cancel?'
      );
      if (!confirmCancel) return;
    }
    handleClose();
  };

  // Group tests by category and type AFTER filtering by activeTab.
  const groupedTests = useMemo(() => {
    // Filter tests based on test_type matching activeTab.
    const filteredTests = labTests.filter(test => {
      // If test_type is missing, default it to "Laboratory"
      const labType = test.lab_type || "Laboratory";
      return labType === activeTab;
    });

    return filteredTests.reduce((acc, test) => {
      if (!acc[test.category]) {
        acc[test.category] = {};
      }
      if (!acc[test.category][test.type]) {
        acc[test.category][test.type] = {};
      }

      // Extract attribute from test name
      const attribute = test.test_name.includes('-AP') ? 'AP' : test.test_name.includes('-LAT') ? 'LAT' : 'None';
      const baseTestName = test.test_name.replace('-AP', '').replace('-LAT', '').trim();
      const sideRequired = test.side_required === true || test.side_required === 1 || test.side_required === '1';

      if (!acc[test.category][test.type][baseTestName]) {
        acc[test.category][test.type][baseTestName] = { 
          test_name: baseTestName, 
          attributes: [],
          is_kit: test.is_kit // Preserve is_kit flag
        };
      }

      if (attribute !== 'None') {
        acc[test.category][test.type][baseTestName].attributes.push({ 
          id: test.id, 
          attribute,
          is_kit: test.is_kit,
          side_required: sideRequired,
        });
      } else {
        acc[test.category][test.type][baseTestName].attributes.push({ 
          id: test.id, 
          attribute: 'None',
          is_kit: test.is_kit,
          side_required: false,
        });
      }

      return acc;
    }, {});
  }, [labTests, activeTab]);

  useEffect(() => {
    setSelectedTests([]);
  }, [activeTab]);

  if (isLoading) {
    return <div>Loading patient details and lab tests...</div>;
  }

  return (
    <div className="medical-lab-widget">
      <ToastContainer />
      <div className="header">
        <div className="labrequest-patient-info">
          {patient ? (
            <>
              <div className="patient-info-row">
                <strong>Patient #:</strong> <span>{patient.patient_number}</span>
              </div>
              <div className="patient-info-row">
                <strong>Name:</strong> <span>{patient.patient_first_name} {patient.patient_last_name}</span>
              </div>
              <div className="patient-info-row">
                <strong>Date of Birth:</strong> <span>{patient.patient_dob}</span>
              </div>
              {hasPermission('/patient-visits/treatment', 'View') && (
                <button 
                  onClick={() => setShowHistory(!showHistory)} 
                  className="btn-patient-history"
                  title="View patient medical history"
                >
                  📋 {showHistory ? 'Hide' : 'View'} History
                </button>
              )}
            </>
          ) : (
            <span>Patient details not found.</span>
          )}
        </div>

        <div className="header-buttons">
          <button onClick={submitRequest} disabled={isSubmitting || selectedTests.length === 0}>
            {isSubmitting ? 'Submitting...' : 'Submit Request'}
          </button>
          <button onClick={cancelRequest} disabled={isSubmitting}>
            Cancel
          </button>
        </div>
      </div>

      {/* Patient History Panel */}
      {showHistory && patient && hasPermission('/patient-visits/treatment', 'View') && (
        <div className="history-panel-container">
          <PatientHistorySummary 
            patientId={patient.patient_id} 
            onClose={() => setShowHistory(false)}
          />
        </div>
      )}

      {/* Branch Section */}
      <div className="branch-input-container">
        <label htmlFor="branchSelect">Branch:</label>
        <select
          id="branchSelect"
          value={branch}
          onChange={(e) => setBranch(e.target.value)}
        >
          <option value="">Select Branch</option>
          <option value="Pultney">Pultney</option>
          <option value="Kissy">Kissy</option>
        </select>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button 
          className={activeTab === "Laboratory" ? "active-tab" : ""}
          onClick={() => setActiveTab("Laboratory")}
        >
          Laboratory
        </button>
        <button 
          className={activeTab === "Radiology" ? "active-tab" : ""}
          onClick={() => setActiveTab("Radiology")}
        >
          Radiology
        </button>
      </div>

      {/* Main Form Section */}
      <div id="mainForm">
        {Object.entries(groupedTests).map(([category, types]) => (
          <div key={category} className="category">
            <h3>{category}</h3>
            <div className="types-container">
              {Object.entries(types).map(([type, tests]) => (
                <div key={type} className="type">
                  <h4>{type}</h4>
                  <div className="tests">
                    {Object.entries(tests)
                      .sort(([, a], [, b]) => {
                        // Sort kits first (is_kit === true comes before is_kit === false)
                        if (a.is_kit && !b.is_kit) return -1;
                        if (!a.is_kit && b.is_kit) return 1;
                        // If both are kits or both are regular tests, maintain alphabetical order
                        return a.test_name.localeCompare(b.test_name);
                      })
                      .map(([baseTestName, { test_name, attributes, is_kit }]) => {
                        // Get historical data for the first attribute (primary test ID)
                        const primaryTestId = attributes[0]?.id;
                        const history = testHistory[primaryTestId];
                        const projectionIds = attributes
                          .filter(({ attribute }) => attribute === 'AP' || attribute === 'LAT')
                          .map(({ id }) => id);
                        const checkedProjections = attributes.filter(({ id, attribute }) => (
                          (attribute === 'AP' || attribute === 'LAT')
                          && selectedTests.some((test) => test.Lab_Test === id)
                        ));
                        const showSharedSide = checkedProjections.some(({ attribute, side_required }) => (
                          projectionNeedsSide(attribute, side_required)
                        ));
                        const sharedSide = selectedTests.find((test) => (
                          checkedProjections.some(({ id }) => id === test.Lab_Test) && test.side
                        ))?.side || null;
                        
                        return (
                      <div key={baseTestName} className="test-group">
                        {attributes.length > 1 ? (
                          <div className="attribute-group">
                            <div className="attribute-row">
                            {attributes.map(({ id, attribute, side_required }) => (
                              <label key={id} className="attr-checkbox-label">
                                {attribute} 
                                <input
                                  type="checkbox"
                                  className="test-checkbox"
                                  data-test-id={id}
                                  data-test-name={`${test_name} - ${attribute}`}
                                  onChange={(e) =>
                                    handleCheckboxChange(
                                      id,
                                      `${test_name} - ${attribute}`,
                                      e.target.checked,
                                      projectionNeedsSide(attribute, side_required),
                                      projectionIds.filter((projectionId) => projectionId !== id)
                                    )
                                  }
                                />
                              </label>
                            ))}
                            <span className="attr-test-name">
                              {is_kit ? '📦 ' : ''}{test_name}
                              {history && hasPermission('/patient-visits/treatment', 'View') && (
                                <span className={`test-history-badge ${
                                  history.request_type === 'Radiology' ? '' : 
                                  test_name.toLowerCase() === 'full blood count' ? getCBCBadgeClass(history) :
                                  checkRangeFlag(history.last_result, history.reference_range).toLowerCase()
                                }`}>
                                  {history.request_type === 'Laboratory' ? (
                                    test_name.toLowerCase() === 'full blood count' ? (
                                      <>{formatCBCBadge(history)}</>
                                    ) : (
                                      <>Last: {history.last_result} ({daysAgo(history.last_date)}d)</>
                                    )
                                  ) : (
                                    <>Requested ({daysAgo(history.last_date)}d)</>
                                  )}
                                </span>
                              )}
                            </span>
                            </div>
                            {showSharedSide && (
                              <SideSelector
                                value={sharedSide}
                                onChange={(side) => handleSideChange(checkedProjections.map(({ id }) => id), side)}
                              />
                            )}
                          </div>
                        ) : (
                          attributes.map(({ id, attribute, is_kit: attrIsKit, side_required }) => {
                            const itemHistory = testHistory[id];
                            const needsSide = projectionNeedsSide(attribute, side_required);
                            const selected = selectedTests.find((test) => test.Lab_Test === id);
                            return (
                            <div key={id} className="test-with-side">
                            <label className="test-checkbox-label">
                              <input
                                type="checkbox"
                                className="test-checkbox"
                                data-test-id={id}
                                data-test-name={test_name}
                                onChange={(e) =>
                                  handleCheckboxChange(
                                    id,
                                    test_name,
                                    e.target.checked,
                                    needsSide
                                  )
                                }
                              />
                              <span className="test-label-text">
                                {attrIsKit ? '📦 ' : ''}{test_name}
                                {itemHistory && hasPermission('/patient-visits/treatment', 'View') && (
                                  <span className={`test-history-badge ${
                                    itemHistory.request_type === 'Radiology' ? '' :
                                    test_name.toLowerCase() === 'full blood count' ? getCBCBadgeClass(itemHistory) :
                                    checkRangeFlag(itemHistory.last_result, itemHistory.reference_range).toLowerCase()
                                  }`}>
                                    {itemHistory.request_type === 'Laboratory' ? (
                                      test_name.toLowerCase() === 'full blood count' ? (
                                        <>{formatCBCBadge(itemHistory)}</>
                                      ) : (
                                        <>Last: {itemHistory.last_result} ({daysAgo(itemHistory.last_date)}d)</>
                                      )
                                    ) : (
                                      <>Requested ({daysAgo(itemHistory.last_date)}d)</>
                                    )}
                                  </span>
                                )}
                              </span>
                            </label>
                            {needsSide && selected && (
                              <SideSelector
                                value={selected.side}
                                onChange={(side) => handleSideChange([id], side)}
                              />
                            )}
                            </div>
                          );
                          })
                        )}
                      </div>
                        );
                      })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LabTestRequestForm;
